import { Box, Button, Flex, Text } from '@ttoss/ui';
import { Icon } from '@iconify/react';
import { LatLng } from '../MapWithSearch/useGeocoding';
import { MapImageOptions, useMapsImage } from './useMapsImage';
import { useGoogleMaps } from '@ttoss/google-maps';
import { useSiflorMap } from '../../providers/SiflorMapProvider/useSiflorMap';
import React from 'react';
import RoundButtonIcon from '../RoundButtonIcon/RoundButtonIcon';

export type MarkPosition = {
  lat: number;
  lng: number;
  icon?: google.maps.Icon;
  onClick?: (latLng: LatLng) => void;
};

export type MapProps = {
  width?: number | string;
  height?: number | string;
  image?: Omit<MapImageOptions, 'map'>;
  options?: google.maps.MapOptions;
  onMarkPin?: (latLng: LatLng) => void;
  markedPositions?: MarkPosition[];
  hideControllers?: boolean;
};

type ClickListenerArgs = {
  latLng: {
    lat: () => number;
    lng: () => number;
  };
};

type MarkerClickEvent = {
  latLng: {
    lat: number;
    lng: number;
  };
};

const defaultOptions: google.maps.MapOptions = {
  zoom: 5,
  restriction: {
    latLngBounds: {
      north: 6.5,
      south: -35.0,
      east: -27.5,
      west: -75.0,
    },
  },
  center: { lat: -15.793889, lng: -47.882778 },
  streetViewControl: false,
  mapTypeId: 'terrain',
  disableDefaultUI: true,
  styles: [
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#404040',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'all',
      stylers: [
        {
          color: '#f2f2f2',
        },
      ],
    },
  ],
};

const USER_LOCATION_MARKER_ICON =
  'https://tt-sandbox-general-purpose.s3.amazonaws.com/location-icon.png';

const Map = ({
  height,
  width,
  image,
  options,
  onMarkPin,
  markedPositions,
  hideControllers,
}: MapProps) => {
  const { googleMaps } = useGoogleMaps();

  const [mapDivRef, setMapDivRef] = React.useState<HTMLElement | null>(null);

  const map = React.useMemo(() => {
    if (googleMaps && mapDivRef) {
      const googleMapsMap = new googleMaps.Map(mapDivRef, defaultOptions);
      return googleMapsMap;
    }

    return null;
  }, [googleMaps, mapDivRef]);

  React.useEffect(() => {
    if (map && options?.center) {
      map.setCenter(options.center);
    }
  }, [map, options?.center]);

  const marker = React.useRef<google.maps.Marker>();

  React.useEffect(() => {
    if (map && googleMaps) {
      marker.current = new googleMaps.Marker({
        map,
        icon: USER_LOCATION_MARKER_ICON,
      });
    }
  }, [map, googleMaps]);

  React.useEffect(() => {
    if (map) {
      map.addListener('click', (args: ClickListenerArgs) => {
        const position = {
          lat: args?.latLng?.lat(),
          lng: args?.latLng?.lng(),
        };

        if (map && position) {
          marker.current?.setPosition(position);
          onMarkPin?.(position);
        }
      });
    }
  }, [map, onMarkPin]);

  React.useEffect(() => {
    if (map && googleMaps && markedPositions?.length > 0) {
      markedPositions.forEach((position) => {
        const customMarker = new googleMaps.Marker({
          map,
          icon: position.icon,
        });
        customMarker.setPosition(position);

        if (position.onClick) {
          googleMaps.event.addListener(
            customMarker,
            'click',
            (markerEventProps: MarkerClickEvent) => {
              position.onClick(markerEventProps.latLng);
            }
          );
        }
      });
    }
  }, [map, googleMaps, markedPositions]);

  React.useEffect(() => {
    if (map && options?.center) {
      marker.current?.setPosition(options.center);
    }
  }, [map, options?.center]);

  const { init: initSiflorMap, isSiflorMapLoaded } = useSiflorMap();

  React.useEffect(() => {
    if (map && isSiflorMapLoaded) {
      initSiflorMap(map);
    }
  }, [map, initSiflorMap, isSiflorMapLoaded]);

  useMapsImage({
    ...image,
    map,
  });

  const handleCenterMap = React.useCallback(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude: lat, longitude: lng } = position.coords;

      map?.setCenter({ lng, lat });
      map?.setZoom(12);
      marker.current?.setPosition({ lng, lat });

      onMarkPin?.({ lng, lat });
    });
  }, [map, onMarkPin]);

  const handleZoomPlus = () => {
    if (!map) {
      return;
    }

    const currentZoom = map.getZoom();

    if (currentZoom) {
      map.setZoom(currentZoom + 1);
    }
  };

  const handleZoomLess = () => {
    if (!map) {
      return;
    }

    const currentZoom = map.getZoom();

    if (currentZoom) {
      map.setZoom(currentZoom - 1);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
      }}
    >
      <Flex
        className="map"
        sx={{
          width: '85vw',
          height: '75vh',
          left: '7.5vw',
          top: 7,
          position: 'absolute',
          zIndex: -1,
        }}
      />
      <Flex
        id="google-maps"
        ref={(r) => {
          return setMapDivRef(r);
        }}
        sx={{ width, height }}
      />

      {!hideControllers && (
        <Flex
          sx={{
            position: 'absolute',
            right: '31px',
            bottom: '120px',
            flexDirection: 'column',
            color: 'background',
            boxShadow: 2,
            fontSize: 4,
          }}
          className="zoom-control"
        >
          <Button
            sx={{
              paddingY: 6,
              paddingX: 5,
              background: 'background',
              color: 'dark',
              borderEndEndRadius: 0,
              borderEndStartRadius: 0,
            }}
            onClick={handleZoomPlus}
          >
            <Text sx={{ fontSize: [3, 4], color: 'dark', display: 'flex' }}>
              <Icon icon="akar-icons:plus" />
            </Text>
          </Button>
          <Button
            sx={{
              paddingY: 6,
              paddingX: 5,
              background: 'background',
              color: 'dark',
              borderStartStartRadius: 0,
              borderStartEndRadius: 0,
              position: 'relative',
              ':before': {
                position: 'absolute',
                content: '""',
                width: '80%',
                height: 1,
                borderRadius: 999,
                backgroundColor: 'muted',
                top: 0,
                left: '10%',
              },
            }}
            onClick={handleZoomLess}
          >
            <Text sx={{ fontSize: [3, 4], color: 'dark', display: 'flex' }}>
              <Icon icon="akar-icons:minus" />
            </Text>
          </Button>
        </Flex>
      )}

      {'geolocation' in navigator && !hideControllers && (
        <RoundButtonIcon
          icon="ic:round-my-location"
          onClick={handleCenterMap}
          aria-label="bottom-right"
          className="current-location-button"
          sx={{
            position: 'absolute',
            right: 7,
            bottom: [9, 8],
          }}
        />
      )}
    </Box>
  );
};

Map.defaultProps = {
  width: 400,
  height: 300,
} as MapProps;

export default React.memo(Map, (prevProps, nextProps) => {
  return (
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.options?.center?.lat === nextProps.options?.center?.lat &&
    prevProps.options?.center?.lng === nextProps.options?.center?.lng &&
    prevProps.onMarkPin === nextProps.onMarkPin
  );
});
